/**
 * SpacerWidget — a pure layout element: intentional blank space.
 *
 * Unlike an empty text block (which publishes as an empty <p>, shows a "Add
 * text…" caret, and boots a Lexical editor), a spacer carries NO content and NO
 * text chrome. It only occupies its grid cell — the grid reserves the area, so
 * resizing the tile IS the height control. In the editor it shows a faint dashed
 * hint so the space reads as intentional; on the published site it renders
 * nothing visible (renderPage emits an empty cell — see the "spacer" case), and
 * in preview/read-only mode it's blank here too.
 */
import { css, html } from "react-strict-dom";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { radii } from "@duro-app/tokens/tokens/spacing.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import type { WidgetProps } from "./types.js";
import { sizes } from "@duro-app/tokens/tokens/sizes.css";

const styles = css.create({
  hint: {
    width: "100%",
    height: "100%",
    minHeight: sizes.chip,
    boxSizing: "border-box",
    borderWidth: borders.hairline,
    borderStyle: "dashed",
    borderColor: colors.border,
    borderRadius: radii.xs,
    backgroundColor: "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  label: {
    fontSize: 11,
    color: colors.textMuted,
    userSelect: "none",
  },
  blank: {
    width: "100%",
    height: "100%",
  },
});

export function SpacerWidget({ contentProps }: WidgetProps) {
  const preview = contentProps?.["preview"] as boolean | undefined;
  // Preview / published-style: pure space, nothing drawn.
  if (preview) return <html.div style={styles.blank} aria-hidden />;
  // Edit mode: a subtle dashed placeholder so authors see the intentional gap.
  return (
    <html.div style={styles.hint} aria-hidden>
      <html.span style={styles.label}>Spacer</html.span>
    </html.div>
  );
}
